import { artworkDigitalDossierMessages } from '@/components/ArtworkDigitalDossierSection/ArtworkDigitalDossier.messages';
import type { ArtworkFormValues } from '@/model/inventory';
import type { ArcualCurrency, ArcualPrice } from '@art-basel/artbasel-lib-platform';
import {
  type ArcualDigitalDossier,
  ArcualDocumentSignatureStatus,
  type ArcualInventoryItem,
  ArcualItemUnit,
} from '@art-basel/artbasel-lib-platform';
import { NeonLabelColor, type NeonLabelModel } from '@art-basel/artbasel-lib-neon';
import { useAmplitudeFeatureFlags } from '@/composables/useAmplitudeFeatureFlags';

const { featureFlags } = useAmplitudeFeatureFlags();

export const getArtworkPayload = (
  formValues: ArtworkFormValues,
  currency: ArcualCurrency,
): Omit<ArcualInventoryItem, 'id' | 'ownerProfileId' | 'sellerProfileId'> => {
  const price = Number(formValues.retailPrice);

  const retailPrice: ArcualPrice = {
    amount: price,
    currency,
  };
  let result: Omit<ArcualInventoryItem, 'id' | 'ownerProfileId' | 'sellerProfileId'> = {
    artistId: formValues.artistId,
    artistName: formValues.artistName,
    artistBirthYear: formValues.artistBirthYear,
    artistNationality: formValues.artistNationality,
    artistBio: formValues.artistBio,
    title: formValues.title,
    year: formValues.year,
    images: formValues.images,
    medium: formValues.medium ? formValues.medium : undefined,
    category: formValues.category,
    dimensions: {
      unit: formValues.unit,
      width: formValues.width ? formValues.width : undefined,
      height: formValues.height ? formValues.height : undefined,
      // depth is undefined if unit is PX
      // and do not send empty string
      depth: formValues.unit === ArcualItemUnit.PX ? undefined : formValues.depth ? formValues.depth : undefined,
      hours: formValues.hours ? formValues.hours : undefined,
      minutes: formValues.minutes ? formValues.minutes : undefined,
      seconds: formValues.seconds ? formValues.seconds : undefined,
      cubicMeters: formValues.cubicMeters ? formValues.cubicMeters : undefined,
      cubicFeet: formValues.cubicFeet ? formValues.cubicFeet : undefined,
      other: formValues.other ? formValues.other : undefined,
    },
    description: formValues.description ? formValues.description : undefined,
    inventoryNumber: formValues.inventoryNumber ? formValues.inventoryNumber : undefined,
  };
  if (!Number.isNaN(price)) {
    result = { ...result, retailPrice, listPrice: retailPrice };
  }
  return result;
};

export const shouldShowPriceOnRequest = (price?: number | string | null): boolean => {
  return price == null || Number.isNaN(price) || price === 0 || price === '0.00';
};

export const getSignatureStatusColor = (status: ArcualDocumentSignatureStatus) => {
  return status === ArcualDocumentSignatureStatus.SIGNED ? NeonLabelColor.Success : NeonLabelColor.Warn;
};

export const getArtworkLabels = (artwork?: ArcualInventoryItem, showPending = false) => {
  const labels: NeonLabelModel[] = [];

  if (!featureFlags.value.isDigitalPortfolioEnabled) return [];

  const validStatuses: Array<ArcualDocumentSignatureStatus> = [ArcualDocumentSignatureStatus.SIGNED];

  if (showPending) validStatuses.push(ArcualDocumentSignatureStatus.PENDING);

  if (
    artwork?.digitalDossierState?.coaSignatureStatus &&
    validStatuses.includes(artwork.digitalDossierState.coaSignatureStatus)
  ) {
    labels.push({
      label: artworkDigitalDossierMessages.coaTitleShort,
      color: getSignatureStatusColor(artwork.digitalDossierState.coaSignatureStatus),
      icon: 'arcual-logo-outline',
    });
  }

  if (artwork?.digitalDossierState?.hasOtherDocuments) {
    labels.push({
      label: artworkDigitalDossierMessages.supportingDocumentsTitle,
      color: NeonLabelColor.Success,
      icon: 'folder-lock-1',
    });
  }

  if (
    artwork?.digitalDossierState?.resaleTermsSignatureStatus &&
    validStatuses.includes(artwork.digitalDossierState.resaleTermsSignatureStatus)
  ) {
    labels.push({
      label: artworkDigitalDossierMessages.resaleTermsTitle,
      color: getSignatureStatusColor(artwork.digitalDossierState.resaleTermsSignatureStatus),
      icon: 'business-contract-handshake-sign',
    });
  }

  return labels;
};

export const isDigitalDossierTransferred = (digitalDossier?: ArcualDigitalDossier) => {
  return (digitalDossier?.transfers?.length ?? 0) > 0;
};
